/**
 * web-account-pages D2 / D3 — the website's messages panel, opened from anywhere
 * on the site: the header's Messages button, the account menu, a message banner,
 * a page's "Message the studio". One panel per site, held by WebLayout.
 */
import { createContext, createElement, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import type { SavedPlace } from '@/hooks/chat/useChatThreadScroll';

import type { WebMessageFilter } from './messageFilters';

export interface OpenMessagesOptions {
  /** Open the panel scrolled to this entry (a message or notice id) instead of the newest. */
  readonly atEntryId?: string;
  /** Open under this filter: the way back from a notice's page (shared-client-screens M22). */
  readonly filter?: WebMessageFilter;
  /** Open at the place the client was reading when they followed a notice's link (M22). */
  readonly restore?: SavedPlace<WebMessageFilter>;
}

export interface MessagesPanelControl {
  /** Opens the messages panel; with `atEntryId`, at that entry; with `filter` and `restore`, where the client left it. */
  readonly openMessages: (opts?: OpenMessagesOptions) => void;
  /** Closes the panel. */
  readonly closeMessages: () => void;
  /** True while the panel is open. */
  readonly open: boolean;
  /** The entry the panel was last asked to open at; null for the newest. */
  readonly atEntryId: string | null;
  /** The filter and place the panel was last asked to open at; null for neither. */
  readonly filter: WebMessageFilter | null;
  readonly restore: SavedPlace<WebMessageFilter> | null;
  /** True while the page shows a header with the client's messages in it (the account piece, or the studio header). */
  readonly headerShown: boolean;
  /** Called by that header while it is on the page; the returned function says it left. */
  readonly showHeader: () => () => void;
}

const noop = () => {};
const closed: MessagesPanelControl = {
  openMessages: noop,
  closeMessages: noop,
  open: false,
  atEntryId: null,
  filter: null,
  restore: null,
  headerShown: false,
  showHeader: () => noop,
};

const PanelContext = createContext<MessagesPanelControl>(closed);

interface PanelState {
  open: boolean;
  atEntryId: string | null;
  filter: WebMessageFilter | null;
  restore: SavedPlace<WebMessageFilter> | null;
}

export function MessagesPanelProvider({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<PanelState>({ open: false, atEntryId: null, filter: null, restore: null });
  const openMessages = useCallback((opts?: OpenMessagesOptions) => {
    setState({ open: true, atEntryId: opts?.atEntryId ?? null, filter: opts?.filter ?? null, restore: opts?.restore ?? null });
  }, []);
  const closeMessages = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  // How many headers with the messages in them are on the page: a page can place the account piece more than once.
  const [headers, setHeaders] = useState(0);
  const showHeader = useCallback(() => {
    setHeaders((n) => n + 1);
    return () => setHeaders((n) => n - 1);
  }, []);
  const value = useMemo<MessagesPanelControl>(
    () => ({ openMessages, closeMessages, ...state, headerShown: headers > 0, showHeader }),
    [openMessages, closeMessages, state, headers, showHeader],
  );
  return createElement(PanelContext.Provider, { value }, children);
}

/** The messages panel's control. Outside WebLayout it does nothing. */
export function useMessagesPanel(): MessagesPanelControl {
  return useContext(PanelContext);
}
